import React, { useState } from 'react';
import {
 Eye,
 TrendingUp,
 TrendingDown,
 AlertCircle,
 Lightbulb,
 Target,
 ListChecks,
} from 'lucide-react';
import { useVendorInsights } from '../../services/vendorAnalyticsService';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const PERIOD_TABS = [
 { value: 'week', label: 'This Week' },
 { value: 'month', label: 'This Month' },
 { value: 'quarter', label: 'This Quarter' },
 { value: 'year', label: 'This Year' },
];

const VendorInsights = () => {
 const [period, setPeriod] = useState('month');

 const { data, isLoading, error } = useVendorInsights(period);

 const insights = data?.data || {};

 const hasKeyInsights = insights.key_insights && insights.key_insights.length > 0;
 const hasTrends = insights.trends && insights.trends.length > 0;
 const hasRecommendations =
 insights.recommendations && insights.recommendations.length > 0;
 const isEmpty = !hasKeyInsights && !hasTrends && !hasRecommendations;

 return (
 <div className="space-y-5">
 <VendorPageHeader
 title="Insights"
 subtitle="Actionable tips to grow your bookings and improve performance."
 />

 <VendorGuideCallout
 title={VENDOR_PAGE_GUIDES.insights.title}
 steps={VENDOR_PAGE_GUIDES.insights.steps}
 icon={ListChecks}
 />

 {/* Period tabs */}
 <div
 className="inline-flex flex-wrap rounded-xl bg-gray-100 p-1 border border-border"
 role="tablist"
 aria-label="Insights period"
 >
 {PERIOD_TABS.map((tab) => {
 const isActive = period === tab.value;
 return (
 <button
 key={tab.value}
 type="button"
 role="tab"
 aria-selected={isActive}
 onClick={() => setPeriod(tab.value)}
 className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${ isActive ? 'bg-surface shadow-sm text-ink' : 'text-ink-secondary hover:text-gray-900' }`}
 >
 {tab.label}
 </button>
 );
 })}
 </div>

 {isLoading ? (
 <div className="space-y-4">
 {[1, 2, 3, 4].map((i) => (
 <div
 key={i}
 className="product-panel p-4 sm:p-6 animate-pulse"
 >
 <div className="h-4 bg-gray-200 rounded w-1/3 mb-3" />
 <div className="h-4 bg-gray-200 rounded w-2/3" />
 </div>
 ))}
 </div>
 ) : error ? (
 <div className="rounded-lg border border-red-200 bg-red-50 text-red-600 px-4 py-3 text-sm flex items-start gap-2.5">
 <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
 <span>Failed to load insights. Please try again.</span>
 </div>
 ) : (
 <>
 {/* Key Insights */}
 {hasKeyInsights && (
 <div className="product-panel p-4 sm:p-6">
 <h2 className="text-lg font-semibold text-ink mb-4 flex items-center gap-2">
 <Lightbulb className="w-5 h-5 text-amber-500" />
 Key Insights
 </h2>
 <div className="space-y-3">
 {insights.key_insights.map((insight, index) => (
 <div
 key={index}
 className="flex items-start gap-3 p-3 rounded-xl bg-surface-muted border border-border"
 >
 <Target className="w-5 h-5 text-ink-secondary flex-shrink-0 mt-0.5" />
 <p className="text-sm text-ink-secondary">{insight}</p>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Trends */}
 {hasTrends && (
 <div className="product-panel p-4 sm:p-6">
 <h2 className="text-lg font-semibold text-ink mb-4">
 Trends
 </h2>
 <div className="space-y-3">
 {insights.trends.map((trend, index) => (
 <div
 key={index}
 className="flex items-center justify-between gap-3 p-3 rounded-xl border border-border"
 >
 <div className="flex items-center gap-3 min-w-0">
 {trend.direction === 'up' ? (
 <TrendingUp className="w-5 h-5 text-emerald-500 flex-shrink-0" />
 ) : (
 <TrendingDown className="w-5 h-5 text-red-500 flex-shrink-0" />
 )}
 <div className="min-w-0">
 <p className="font-medium text-ink truncate">
 {trend.metric}
 </p>
 <p className="text-sm text-ink-tertiary">
 {trend.description}
 </p>
 </div>
 </div>
 <span
 className={`px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 border ${ trend.direction === 'up' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-red-50 text-red-800 border-red-200' }`}
 >
 {trend.change}
 </span>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Recommendations */}
 {hasRecommendations && (
 <div className="product-panel p-4 sm:p-6">
 <h2 className="text-lg font-semibold text-ink mb-4 flex items-center gap-2">
 <AlertCircle className="w-5 h-5 text-amber-500" />
 Recommendations
 </h2>
 <div className="space-y-3">
 {insights.recommendations.map((recommendation, index) => (
 <div
 key={index}
 className="flex items-start gap-3 p-3 rounded-xl bg-amber-50/70 border border-amber-100"
 >
 <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
 <div>
 <p className="font-medium text-ink mb-1">
 {recommendation.title}
 </p>
 <p className="text-sm text-ink-secondary">
 {recommendation.description}
 </p>
 </div>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Empty State */}
 {isEmpty && (
 <div className="product-panel p-8 sm:p-12 text-center">
 <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-lg border border-border bg-surface-muted text-gray-400">
 <Eye className="w-7 h-7" />
 </div>
 <h3 className="text-base font-semibold text-ink">
 No insights available
 </h3>
 <p className="mt-1.5 text-sm text-ink-tertiary">
 No insights available for this period. Check back later for actionable insights.
 </p>
 </div>
 )}
 </>
 )}
 </div>
 );
};

export default VendorInsights;
